import React, { useState } from 'react';
import { LoadingButton } from '@atlaskit/button';
import ChevronDownIcon from '@atlaskit/icon/glyph/chevron-down';
import Popup from '@atlaskit/popup';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import { fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { CompassComponentsSpotlight } from '../compass-components-spotlight/compass-components-spotlight.tsx';
import messages from './messages';
import { PopupContent } from './popup-content';

type Props = {
	testId?: string;
	loading: boolean;
	enabled: boolean | undefined;
	setProperty: (newValue: boolean) => void;
	isAdminContext: boolean;
	toggleOpen: (isOpen: boolean) => void;
	isOpen: boolean;
};

type WrappedGlobalComponentsToggleButtonProps = {
	globalComponentsProperty: {
		loading: boolean;
		enabled: boolean;
		setProperty: (newValue: boolean) => void;
	};
	isEditable: boolean;
};

export function GlobalComponentsToggleButton(props: Props) {
	const { testId, loading, enabled, setProperty, isAdminContext, isOpen, toggleOpen } = props;
	const { formatMessage } = useIntl();

	return (
		<Popup
			isOpen={isOpen}
			onClose={() => toggleOpen(false)}
			placement="bottom-end"
			content={() => (
				<PopupContent
					isAdminContext={isAdminContext}
					loading={loading}
					globalComponentsEnabled={!!enabled}
					setGlobalComponentsProperty={setProperty}
				/>
			)}
			trigger={(triggerProps) => (
				<LoadingButton
					{...triggerProps}
					testId={testId}
					isLoading={loading}
					isSelected={isOpen}
					onClick={(_, analyticsEvent) => {
						fireUIAnalytics(analyticsEvent, 'globalComponentsToggleButton', {
							opened: !isOpen,
						});
						toggleOpen(!isOpen);
					}}
					iconAfter={<ChevronDownIcon label="" />}
				>
					{formatMessage(messages.productComponents, {
						product: enabled ? 'Compass' : 'Jira',
					})}
				</LoadingButton>
			)}
		/>
	);
}

export function WrappedGlobalComponentsToggleButton(
	props: WrappedGlobalComponentsToggleButtonProps,
) {
	const { globalComponentsProperty, isEditable } = props;
	const [isGlobalComponentsPopupOpen, setIsGlobalComponentsPopupOpen] = useState(false);
	// https://switcheroo.atlassian.com/ui/gates/1b168ef2-2b46-4911-a34c-48247c85f7b9
	const compassComponentsUpsellAdEnabled = !fg('compass-components-upsell-ad');

	return (
		<JSErrorBoundary
			fallback="unmount"
			packageName="jiraProjectDirectoriesComponents"
			teamName="compass-lodestone"
			id="wrappedGlobalComponentsToggleButton"
			sendToPrivacyUnsafeSplunk
		>
			{compassComponentsUpsellAdEnabled ? (
				<CompassComponentsSpotlight
					testId="project-directories.components.global-components-toggle-button.compass-components-spotlight"
					targetRadius={3}
					dialogPlacement="bottom right"
					isVisible={!globalComponentsProperty?.loading && !globalComponentsProperty?.enabled}
					onDismiss={(_, analyticEvent) => {
						fireUIAnalytics(analyticEvent, 'compassComponentsSpotlightDismiss');
					}}
					onLearnMore={(e, analyticEvent) => {
						e.stopPropagation();
						setIsGlobalComponentsPopupOpen(true);
						fireUIAnalytics(analyticEvent, 'compassComponentSpotlightLearnMore');
					}}
				>
					<GlobalComponentsToggleButton
						toggleOpen={setIsGlobalComponentsPopupOpen}
						isOpen={isGlobalComponentsPopupOpen}
						testId="project-directories.components.global-components-toggle-button.compass-global-components-toggle-button"
						isAdminContext={isEditable}
						{...globalComponentsProperty}
					/>
				</CompassComponentsSpotlight>
			) : (
				<GlobalComponentsToggleButton
					toggleOpen={setIsGlobalComponentsPopupOpen}
					isOpen={isGlobalComponentsPopupOpen}
					testId="project-directories.components.global-components-toggle-button.compass-global-components-toggle-button"
					isAdminContext={isEditable}
					{...globalComponentsProperty}
				/>
			)}
		</JSErrorBoundary>
	);
}
